import { ReactNode } from 'react';
import { cn } from '@/lib/utils';

export interface GroupedSectionProps {
  /** The main title for the settings section */
  title: string;
  /** Optional description text */
  description?: string;
  /** Content to display in the settings area */
  children: ReactNode;
  /** Additional CSS classes */
  className?: string;

  vertical?: boolean;
  variant?: 'default' | 'subtle' | 'danger' | 'plain';
}

export function GroupedSection({
  title,
  description,
  children,
  className,
  vertical,
  variant = 'default',
}: GroupedSectionProps) {
  return (
    <section
      className={cn(
        'flex w-full flex-col justify-between gap-8 rounded-xl border border-transparent px-8 py-10 xl:flex-row',
        className,
        vertical && 'xl:flex-col',
        {
          'border-green-darker bg-green-darker': variant === 'default',
          'border-red-500/20 bg-red-950/20': variant === 'danger',
          'border-gray-500/20 bg-gray-950/20': variant === 'subtle' || variant === 'default',
          'rounded-none': variant === 'plain',
        }
      )}
    >
      <div className="space-y-2">
        <h2 className="text-xl font-semibold">{title}</h2>
        {description && <p className="max-w-[600px] text-muted/60">{description}</p>}
      </div>
      <div
        className={cn('flex w-full flex-col gap-4 xl:max-w-[900px]', vertical && 'xl:max-w-full')}
      >
        {children}
      </div>
    </section>
  );
}

export const GroupedSubsection = ({ children, title }: { children: ReactNode; title?: string }) => {
  return (
    <div className="flex flex-col gap-4 rounded-md border border-dashed border-muted/30 p-6">
      {title && <h3 className="text-lg font-bold">{title}</h3>}
      {children}
    </div>
  );
};
